import { zodResolver } from '@hookform/resolvers/zod'
import {
  ApiError,
  type CreateProductCategoryInput,
  createProductCategorySchema,
  type ProductCategory,
  type ProductType,
  queryKeys,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Tags } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { AiAssistButton } from '../components/ai-assist.js'
import { Page, PageHeader } from '../components/app-shell.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

/**
 * The shelves of the shop.
 *
 * These are headings customers read, not an internal taxonomy, which is why
 * they are a managed list: free text produced "Accessories" and "accesories"
 * side by side on the storefront. Renaming one here carries its products with
 * it, and deleting one that still holds products asks where they should go
 * rather than leaving them filed under nothing.
 */
export function CategoriesPage() {
  const { can } = useAuth()
  const canWrite = can('catalog:write')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<ProductCategory | null>(null)
  const [deleting, setDeleting] = useState<ProductCategory | null>(null)

  const { data: categories, isLoading } = useQuery({
    queryKey: queryKeys.products.categories(),
    queryFn: () => api.get<ProductCategory[]>('/categories'),
  })

  return (
    <Page>
      <PageHeader
        title="Categories"
        description="The headings the shop is browsed by. Products can only be filed under one of these."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus /> New category
            </Button>
          ) : null
        }
      />

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                {['Name', 'Description', 'Products', 'Order', 'Shown', ''].map((heading) => (
                  <th
                    key={heading}
                    className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? null : categories && categories.length > 0 ? (
                categories.map((category) => (
                  <tr key={category.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">{category.name}</td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {category.description ?? '—'}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{category.productCount}</td>
                    <td className="px-3 py-2 tabular-nums text-foreground-muted">
                      {category.sortOrder}
                    </td>
                    <td className="px-3 py-2">{category.isActive ? 'Yes' : 'Hidden'}</td>
                    <td className="px-3 py-2">
                      {canWrite ? (
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setEditing(category)}
                          >
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleting(category)}>
                            Delete
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      icon={Tags}
                      title="No categories yet"
                      description="Add the headings this shop is browsed by — phones, plans, accessories."
                      action={
                        canWrite
                          ? { label: 'Add a category', onClick: () => setCreating(true) }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateCategoryDialog open={creating} onOpenChange={setCreating} />
      <EditCategoryDialog category={editing} onClose={() => setEditing(null)} />
      <DeleteCategoryDialog
        category={deleting}
        others={(categories ?? []).filter((c) => c.id !== deleting?.id)}
        onClose={() => setDeleting(null)}
      />
    </Page>
  )
}

type FormValues = z.input<typeof createProductCategorySchema>

/** Mirrors Radix's dislike of an empty item value — see UNCATEGORISED in products.tsx. */
const ANY_TYPE = '__any__'

const TYPE_LABEL: Record<ProductType, string> = {
  serialized: 'Phones (tracked by IMEI)',
  stock: 'Counted stock',
  plan: 'Service plans (recurring)',
  service: 'Services (one-off)',
  sim: 'SIM cards (tracked by ICCID)',
  gift_card: 'Gift cards (stored value)',
  digital_license: 'Digital licenses (tracked by license number)',
}

function CreateCategoryDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues, unknown, CreateProductCategoryInput>({
    resolver: zodResolver(createProductCategorySchema),
    defaultValues: { sortOrder: 100, isActive: true, allowedType: null },
  })

  useEffect(() => {
    if (!open) reset({ sortOrder: 100, isActive: true, allowedType: null })
  }, [open, reset])

  const onSubmit = handleSubmit(async (values) => {
    try {
      await api.post('/categories', values)
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Category created')
      onOpenChange(false)
    } catch (error) {
      toast.error(
        'Could not create the category',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New category</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <Field label="Name" error={errors.name?.message} required>
              {(props) => <Input {...props} {...register('name')} invalid={!!errors.name} />}
            </Field>

            <Field
              label="Description"
              error={errors.description?.message}
              hint="The line under the heading on the shop page. Leave blank for none."
            >
              {(props) => (
                <div className="relative">
                  <Textarea
                    {...props}
                    rows={2}
                    className="pe-8"
                    {...register('description', {
                      setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                    })}
                  />
                  <div className="absolute end-1.5 top-1.5">
                    <AiAssistButton
                      target="plain-text"
                      value={watch('description') ?? null}
                      onApply={(text) =>
                        setValue('description', text, { shouldValidate: true, shouldDirty: true })
                      }
                      context={{
                        fieldLabel: 'category description',
                        hints: { Category: watch('name') },
                        maxLength: 300,
                      }}
                    />
                  </div>
                </div>
              )}
            </Field>

            <Field
              label="Order"
              error={errors.sortOrder?.message}
              hint="Low numbers come first on the shop page."
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  invalid={!!errors.sortOrder}
                />
              )}
            </Field>

            <Field
              label="Products here are"
              hint={
                '"Any type" leaves it open; picking one keeps the wrong kind of product off this shelf.'
              }
            >
              {(props) => (
                <Select
                  value={watch('allowedType') ?? ANY_TYPE}
                  onValueChange={(value) =>
                    setValue('allowedType', value === ANY_TYPE ? null : (value as ProductType))
                  }
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_TYPE}>Any type</SelectItem>
                    {(Object.keys(TYPE_LABEL) as ProductType[]).map((t) => (
                      <SelectItem key={t} value={t}>
                        {TYPE_LABEL[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Create category
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditCategoryDialog({
  category,
  onClose,
}: {
  category: ProductCategory | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sortOrder, setSortOrder] = useState('100')
  const [isActive, setIsActive] = useState(true)
  const [allowedType, setAllowedType] = useState<string>(ANY_TYPE)

  useEffect(() => {
    if (!category) return
    setName(category.name)
    setDescription(category.description ?? '')
    setSortOrder(String(category.sortOrder))
    setIsActive(category.isActive)
    setAllowedType(category.allowedType ?? ANY_TYPE)
  }, [category])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/categories/${category?.id}`, {
        name: name.trim(),
        description: description.trim() || null,
        sortOrder: Number(sortOrder),
        isActive,
        allowedType: allowedType === ANY_TYPE ? null : allowedType,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const renaming = category !== null && name.trim() !== category.name

  return (
    <Dialog open={category !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {category?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>

          {renaming && (category?.productCount ?? 0) > 0 ? (
            <p className="text-xs text-foreground-muted">
              {category?.productCount} {category?.productCount === 1 ? 'product' : 'products'} will
              move to the new name.
            </p>
          ) : null}

          <Field label="Description">
            {(props) => (
              <div className="relative">
                <Textarea
                  {...props}
                  rows={2}
                  className="pe-8"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <div className="absolute end-1.5 top-1.5">
                  <AiAssistButton
                    target="plain-text"
                    value={description || null}
                    onApply={setDescription}
                    context={{
                      fieldLabel: 'category description',
                      hints: { Category: name },
                      maxLength: 300,
                    }}
                  />
                </div>
              </div>
            )}
          </Field>

          <Field label="Order" hint="Low numbers come first on the shop page.">
            {(props) => (
              <Input
                {...props}
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Products here are"
            hint={
              '"Any type" leaves it open; picking one keeps the wrong kind of product off this shelf.'
            }
          >
            {(props) => (
              <Select value={allowedType} onValueChange={setAllowedType}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_TYPE}>Any type</SelectItem>
                  {(Object.keys(TYPE_LABEL) as ProductType[]).map((t) => (
                    <SelectItem key={t} value={t}>
                      {TYPE_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <div className="flex items-start gap-3">
            <Switch id="category-active" checked={isActive} onCheckedChange={setIsActive} />
            <label className="text-sm" htmlFor="category-active">
              Offer this category when filing a product
              <span className="block text-xs text-foreground-muted">
                Turning it off hides it from the picker. Products already filed here stay where they
                are.
              </span>
            </label>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Deleting is where the damage would be, so the products are counted first and
 * somewhere to put them is asked for rather than assumed.
 */
function DeleteCategoryDialog({
  category,
  others,
  onClose,
}: {
  category: ProductCategory | null
  others: ProductCategory[]
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [moveTo, setMoveTo] = useState<string>('')

  useEffect(() => setMoveTo(''), [])

  const occupied = (category?.productCount ?? 0) > 0

  const remove = useMutation({
    mutationFn: () =>
      api.delete(
        `/categories/${category?.id}${occupied ? `?moveTo=${encodeURIComponent(moveTo)}` : ''}`,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Category deleted')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not delete', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={category !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {category?.name}?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {occupied ? (
            <>
              <p className="text-sm">
                {category?.productCount}{' '}
                {category?.productCount === 1 ? 'product is' : 'products are'} filed here. Choose
                where they should go.
              </p>
              <Field label="Move products to" required>
                {(props) => (
                  <Select value={moveTo} onValueChange={setMoveTo}>
                    <SelectTrigger {...props}>
                      <SelectValue placeholder="Choose a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {others.map((other) => (
                        <SelectItem key={other.id} value={other.name}>
                          {other.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </>
          ) : (
            <p className="text-sm">Nothing is filed here, so nothing else changes.</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => remove.mutate()}
            disabled={remove.isPending || (occupied && !moveTo)}
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
